import BaseController from "./base_controller"
import { t } from "@/utils/i18n"

// Connects to data-controller="entry-form"
export default class extends BaseController {
  static targets = ["moodRating", "energyRating", "sleepRating", "anxietyRating",
                   "focusRating", "creativityRating", "socialRating", "supportRing"]
  static values = {
    draftKey: String
  }

  declare readonly moodRatingTarget: HTMLElement
  declare readonly energyRatingTarget: HTMLElement
  declare readonly sleepRatingTarget: HTMLElement
  declare readonly anxietyRatingTarget: HTMLElement
  declare readonly focusRatingTarget: HTMLElement
  declare readonly creativityRatingTarget: HTMLElement
  declare readonly socialRatingTarget: HTMLElement

  declare readonly supportRingTarget: HTMLDetailsElement
  declare readonly hasSupportRingTarget: boolean

  declare draftKeyValue: string
  declare hasDraftKeyValue: boolean

  private _submitHandler: ((event: Event) => void) | null = null

  connect(): void {
    super.connect()
    this.debug('connect', { hasForm: !!this.element.querySelector('form') })
    this.initializeRatings()
    if (this.isEditMode()) {
      this.loadDraft()
    }
    if (this.hasSupportRingTarget && window.matchMedia?.('(min-width: 768px)').matches) {
      this.supportRingTarget.open = true
    }
    this.bindEvents()
    this.bindFormSubmission()
  }

  disconnect(): void {
    super.disconnect()
    this.saveDraft()
  }

  private initializeRatings(): void {
    // Ratings are displayed directly as stars - no initialization needed
  }

  private bindEvents(): void {
    const form = this.formElement()
    if (form) {
      const debouncedSave = this.debounce(() => this.saveDraft(), 1000)
      form.addEventListener('input', debouncedSave as EventListener)
      form.addEventListener('change', debouncedSave as EventListener)
    }
  }

  private bindFormSubmission(): void {
    const form = this.formElement()

    if (form) {
      if (this._submitHandler) {
        form.removeEventListener('submit', this._submitHandler)
      }

      this._submitHandler = (event: Event) => {
        this.debug('submit event triggered')
        this.handleFormSubmit(event)
      }

      this.debug('bindFormSubmission -> attaching submit listener')
      form.addEventListener('submit', this._submitHandler)
    } else {
      this.debug('bindFormSubmission -> no form found')
    }
  }

  toggleDoseFields(event: Event): void {
    const doseContent = this.element.querySelector<HTMLElement>('#dose-content')
    const tookDose = (event.target as HTMLInputElement).checked
    this.debug('toggleDoseFields', { checked: tookDose })

    if (doseContent) {
      if (tookDose) {
        doseContent.classList.remove('hidden')
      } else {
        doseContent.classList.add('hidden')
        doseContent.querySelectorAll<HTMLInputElement | HTMLSelectElement>('input, select').forEach(field => {
          if (field.type !== 'checkbox') field.value = ''
        })
      }
    }
  }

  private saveDraft(): void {
    try {
      const form = this.formElement()
      if (!form) return
      
      const formData = new FormData(form)
      const draftData: Record<string, string> = {}
      
      for (let [key, value] of formData.entries()) {
        draftData[key] = String(value)
      }
      
      localStorage.setItem(this.draftStorageKey(), JSON.stringify(draftData))
    } catch (error) {
      this.handleError(error, 'saveDraft')
    }
  }

  private loadDraft(): void {
    try {
      const draftData = localStorage.getItem(this.draftStorageKey())
      if (!draftData) return
      
      const data = JSON.parse(draftData)
      
      Object.entries(data).forEach(([key, value]) => {
        const field = this.element.querySelector<HTMLInputElement>(`[name="${key}"]`)
        if (field) {
          if (field.type === 'radio') {
            const radioButton = this.element.querySelector<HTMLInputElement>(`[name="${key}"][value="${value}"]`)
            if (radioButton) radioButton.checked = true
          } else {
            field.value = String(value)
          }
        }
      })
      
      this.initializeRatings()
      this.showInfoMessage(t('draft_loaded'))
      
    } catch (error) {
      this.handleError(error, 'loadDraft')
      localStorage.removeItem(this.draftStorageKey())
    }
  }

  private clearDraft(): void {
    try {
      localStorage.removeItem(this.draftStorageKey())
    } catch (error) {
      this.handleError(error, 'clearDraft')
    }
  }

  private validateForm(): boolean {
    try {
      const form = this.formElement()
      if (!form) {
        this.debug('validateForm -> no form found')
        return false
      }

      const requiredFields = form.querySelectorAll<HTMLInputElement>('[required]')

      for (let field of requiredFields) {
        if (!field.value.trim()) {
          const section = field.closest('details')
          if (section) section.open = true
          field.focus()
          this.showError(t('please_fill_in', { name: field.name }))
          return false
        }
      }

      const tookDose = form.querySelector<HTMLInputElement>('input[type="checkbox"][name="entry[took_dose]"]')?.checked

      if (tookDose) {
        const doseAmount = form.querySelector<HTMLInputElement>('input[name="entry[dose_amount]"]')?.value

        if (!doseAmount) {
          form.querySelector<HTMLElement>('#dose-content')?.classList.remove('hidden')
          form.querySelector<HTMLInputElement>('input[name="entry[dose_amount]"]')?.focus()
          this.showError(t('please_enter_dose'))
          return false
        }
      }

      return true
    } catch (error) {
      this.handleError(error, 'validateForm')
      return false
    }
  }

  private handleFormSubmit(event: Event): void {
    try {
      const isValid = this.validateForm()
      this.debug('handleFormSubmit', { isValid })
      if (!isValid) {
        this.debug('Form validation failed, preventing submission')
        event.preventDefault()
        return
      }
      
      this.clearDraft()
    } catch (error) {
      this.handleError(error, 'handleFormSubmit')
      event.preventDefault()
    }
  }

  setMetric(metricName: string, value: string): void {
    const input = this.element.querySelector<HTMLInputElement>(`input[name="entry[${metricName}]"][value="${value}"]`)
    if (input) {
      input.checked = true
    }
  }

  clearForm(): void {
    const form = this.formElement()
    if (form) {
      form.reset()
      this.initializeRatings()
      this.clearDraft()
    }
  }

  private showError(message: string): void {
    this.showErrorMessage(message)
  }

  private formElement(): HTMLFormElement | null {
    if (this.element.matches('form')) {
      return this.element as HTMLFormElement
    }

    return this.element.querySelector<HTMLFormElement>('form')
  }

  private draftStorageKey(): string {
    if (this.hasDraftKeyValue && this.draftKeyValue) {
      return this.draftKeyValue
    }

    return 'entry_form_draft'
  }

  private isEditMode(): boolean {
    const draftKey = this.draftStorageKey()
    return draftKey.includes('_edit_')
  }
}

